import { useMemo, useState } from 'react';
import { useToast } from '../../../helpers/hooks/useToast';
import { useSelector } from 'react-redux';
import { configureUrlWithParams } from '../../../helpers/utils';
import { fetchData } from '../../../helpers/offthreadFetcher';
import type { RootState } from '../../../state/store';

interface Params {
  dashboard: string;
  dashboardWidget: string;
  widgetUrl: string;
  filters: any;
}

const useFetchAiSuggestions = ({ dashboard, dashboardWidget, widgetUrl, filters }: Params) => {
  const { company } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details?.time_zone_offset;
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = useMemo(() => {
    if (dashboard && dashboardWidget && widgetUrl) {
      return configureUrlWithParams(`/api/v1/dar/${dashboard}/${dashboardWidget}/${widgetUrl}/ai`, {
        ...filters,
        timezoneOffset,
        shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange
      });
    }
    return null;
  }, [
    dashboard,
    dashboardWidget,
    widgetUrl,
    JSON.stringify(filters),
    timezoneOffset,
    company.shouldUseEpocForTimeRange
  ]);

  const reloadData = async () => {
    if (!apiUrl) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchData(apiUrl, { method: 'GET' });
      setData(result);
    } catch (err) {
      setError(err as any);
      showToast({
        message: 'Oops! Unable to load AI suggestions. Please try again later',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetData = () => {
    setData(null);
    setError(null);
  };

  return { data, isLoading, error, reloadData, resetData };
};

export { useFetchAiSuggestions };
